import React, { useState } from 'react';
import { FiX, FiStar, FiSend } from 'react-icons/fi';

/**
 * FeedbackForm - Modal for submitting session feedback
 *
 * Collects:
 * - Overall score (1-5 stars)
 * - Text feedback
 * - Would recommend? (yes/no)
 */

const FeedbackForm = ({ sessionId, onClose, onSubmit, isSubmitting }) => {
  const [score, setScore] = useState(0);
  const [hoveredScore, setHoveredScore] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (score === 0) {
      newErrors.score = 'Please select a rating';
    }

    if (wouldRecommend === null) {
      newErrors.wouldRecommend = 'Please indicate if you would recommend this service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      session_id: sessionId,
      score,
      feedback_text: feedback.trim() || null,
      would_recommend: wouldRecommend,
    });
  };

  const renderStar = (starValue) => {
    const isActive = (hoveredScore || score) >= starValue;

    return (
      <button
        key={starValue}
        type="button"
        onMouseEnter={() => setHoveredScore(starValue)}
        onMouseLeave={() => setHoveredScore(0)}
        onClick={() => {
          setScore(starValue);
          if (errors.score) setErrors((prev) => ({ ...prev, score: null }));
        }}
        className="p-1 focus:outline-none focus:ring-2 focus:ring-amber-400 rounded transition-transform hover:scale-110"
      >
        <FiStar
          className={`w-8 h-8 transition-colors ${
            isActive
              ? 'text-amber-400 fill-amber-400'
              : 'text-gray-300 dark:text-gray-600'
          }`}
        />
      </button>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Share Your Feedback
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Help us improve the matching experience
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Star Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              How would you rate this session? <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(renderStar)}
            </div>
            {score > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {score === 1 && 'Poor'}
                {score === 2 && 'Fair'}
                {score === 3 && 'Good'}
                {score === 4 && 'Very Good'}
                {score === 5 && 'Excellent'}
              </p>
            )}
            {errors.score && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.score}</p>
            )}
          </div>

          {/* Feedback Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Comments
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              placeholder="Share any additional thoughts about your experience..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 focus:border-transparent resize-none"
            />
          </div>

          {/* Would Recommend */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Would you recommend this introduction service to others? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setWouldRecommend(true);
                  if (errors.wouldRecommend) setErrors((prev) => ({ ...prev, wouldRecommend: null }));
                }}
                className={`
                  flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors
                  ${wouldRecommend === true
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }
                `}
              >
                Yes, definitely
              </button>
              <button
                type="button"
                onClick={() => {
                  setWouldRecommend(false);
                  if (errors.wouldRecommend) setErrors((prev) => ({ ...prev, wouldRecommend: null }));
                }}
                className={`
                  flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors
                  ${wouldRecommend === false
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }
                `}
              >
                Not at this time
              </button>
            </div>
            {errors.wouldRecommend && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.wouldRecommend}</p>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 rounded-lg transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FiSend className="w-4 h-4" />
                  Submit Feedback
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;
